import React, { useState, useEffect, useCallback, useRef } from 'react'
import LeftSidebar from './LeftSidebar'
import BottomFilmstrip from './BottomFilmstrip'
import RightPanel from './right-panel/RightPanel'
import ImagePreview from './ImagePreview'
import NewPhotoPanel from './NewPhotoPanel'
import { Plus } from 'lucide-react'
import generationsApi from '../../services/generationsApi'
import StatusBar from './StatusBar'
import useEditStore from '../../controllers/EditController'
import PromptBar from './PromptBar'
import { AnimatePresence } from 'framer-motion'
import ToastContainer from '../shared/ToastContainer'
import useToastStore from '../../stores/toastStore'
import { imageLoadingQueue } from '../../utils/imageLoadingQueue'
import { removeThumbnailFromCache } from '../../utils/thumbnailCache'

function Editor() {
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [groups, setGroups] = useState([])
  const [showNewPhotoPanel, setShowNewPhotoPanel] = useState(false)
  const [rightPanelVisible, setRightPanelVisible] = useState(true)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })
  const canvasSizeRef = useRef({ width: 0, height: 0 })
  const [imageError, setImageError] = useState(null)
  const [currentAdjustments, setCurrentAdjustments] = useState(null)
  const imagePreviewRef = useRef(null)
  const [brushLayer, setBrushLayer] = useState(null)
  const [isBrushing, setIsBrushing] = useState(false)
  const [maskLayer, setMaskLayer] = useState(null)
  const [brushMode, setBrushMode] = useState('brush')
  const { 
    setImagePreviewRef,
    handleStroke,
    completePainting,
    isPainting,
    brushStrokes,
    currentStrokeIndex,
    reset,
    undo,
    redo
  } = useEditStore()
  const addToast = useToastStore(state => state.addToast)
  const [showMaskPreview, setShowMaskPreview] = useState(false)
  const [selectedMaskId, setSelectedMaskId] = useState(null)

  // Load groups on mount
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const response = await generationsApi.getAll()
        setGroups(response)
      } catch (error) {
        console.error('Failed to load groups:', error)
      }
    }

    loadGroups()
  }, [])

  // Handle new generations
  const handleGenerate = async (pendingGenerations) => {
    // Create temporary group entries for pending generations
    const newGroups = pendingGenerations.map(gen => ({
      groupId: gen._id,
      originalGenerationId: gen._id,
      prompt: gen.originalPrompt,
      orientation: gen.orientation,
      style: gen.style,
      createdAt: new Date().toISOString(),
      lastUpdatedAt: new Date().toISOString(),
      totalVersions: 1,
      versions: [{
        ...gen,
        id: gen._id,
        status: 'processing',
        versionNumber: 0,
        isOriginalVersion: true
      }]
    }))
    
    setGroups(prev => [...newGroups, ...prev])
    setShowNewPhotoPanel(false)
    
    if (newGroups.length > 0) {
      setSelectedGroup(newGroups[0])
      localStorage.setItem('lastSelectedGroupId', newGroups[0].groupId)
    }
  }

  // First define selectGroup
  const selectGroup = useCallback((group) => {
    if (!group) {
      setSelectedGroup(null)
      localStorage.removeItem('lastSelectedGroupId')
      return
    }

    if (group.versions.every(v => v.status === 'processing')) {
      return
    }

    localStorage.setItem('lastSelectedGroupId', group.groupId)

    // If a specific version is selected, use that, otherwise use latest completed version
    const versionToUse = group.selectedVersion || 
      [...group.versions]
        .sort((a, b) => b.versionNumber - a.versionNumber)
        .find(v => v.status === 'completed')

    if (versionToUse) {
      // Create a new group object with the selected version and source URL
      const selectedGroup = {
        ...group,
        selectedVersion: {
          ...versionToUse,
          src: `${process.env.REACT_APP_API_URL}/generations/image/${versionToUse.id}`
        }
      }
      
      console.log('Setting selected group:', selectedGroup)
      setSelectedGroup(selectedGroup)
    }
  }, [])


  // Polling effect for processing versions
  useEffect(() => {
    const processingGroups = groups.filter(group => 
      group.versions.some(v => v.status === 'processing')
    )

    if (processingGroups.length === 0) return

    let isPolling = true
    let timeoutId = null

    const pollGenerationStatus = async () => {
      if (!isPolling) return

      try {
        const processingIds = processingGroups
          .flatMap(group => group.versions)
          .filter(version => version.status === 'processing')
          .map(version => version.id)
          .filter(id => typeof id === 'string' && id.length === 24)

        if (processingIds.length === 0) return

        const response = await generationsApi.checkStatus(processingIds)
        
        if (response.length > 0) {
          setGroups(prev => {
            const updated = [...prev]
            response.forEach(updatedVersion => {
              const groupIndex = updated.findIndex(g => 
                g.versions.some(v => v.id === updatedVersion._id)
              )
              
              if (groupIndex !== -1) {
                const versionIndex = updated[groupIndex].versions.findIndex(v => 
                  v.id === updatedVersion._id
                )
                
                if (versionIndex !== -1) {
                  updated[groupIndex].versions[versionIndex] = {
                    ...updated[groupIndex].versions[versionIndex],
                    ...updatedVersion,
                    status: updatedVersion.status,
                    id: updatedVersion._id
                  }

                  // If this version just completed and belongs to the selected group
                  // or if this is a new generation (first completed version)
                  if (updatedVersion.status === 'completed' && 
                      (!selectedGroup || updated[groupIndex].groupId === selectedGroup?.groupId)) {
                    // Force thumbnail refresh
                    removeThumbnailFromCache(updatedVersion._id)
                    imageLoadingQueue.removeFromQueues(updatedVersion._id)
                    
                    // Force the FilmstripItem to remount by updating the group's status
                    updated[groupIndex] = {
                      ...updated[groupIndex],
                      lastUpdatedAt: new Date().toISOString(), // Force re-render
                      versions: updated[groupIndex].versions.map(v => 
                        v.id === updatedVersion._id 
                          ? { ...v, status: 'completed', _forceRefresh: Date.now() }
                          : v
                      )
                    }
                    
                    // If this is the selected group, update its selected version as well
                    if (selectedGroup && updated[groupIndex].groupId === selectedGroup.groupId) {
                      selectGroup(updated[groupIndex])
                    }
                  }
                }
              }
            })
            return updated
          })
        }

        // Schedule next poll with a single timeout if still processing
        if (isPolling && processingGroups.length > 0) {
          timeoutId = setTimeout(pollGenerationStatus, 2000)
        }
      } catch (error) {
        console.error('Failed to check generation status:', error)
        if (isPolling) {
          timeoutId = setTimeout(pollGenerationStatus, 5000)
        }
      }
    }

    // Start polling
    pollGenerationStatus()

    // Cleanup function
    return () => {
      isPolling = false
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [groups, selectedGroup, selectGroup])

  // Then use it in useEffect
  useEffect(() => {
    const lastSelectedId = localStorage.getItem('lastSelectedGroupId')
    
    if (lastSelectedId && groups.length > 0) {
      const savedGroup = groups.find(g => g.groupId === lastSelectedId)
      if (savedGroup && savedGroup.versions.some(v => v.status === 'completed')) {
        selectGroup(savedGroup)
      } else {
        localStorage.removeItem('lastSelectedGroupId')
      }
    }
  }, [groups, selectGroup])

  const handleAddClick = () => {
    setShowNewPhotoPanel(true)
  }

  // Update canvas size effect
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.querySelector('.canvas-container')
      if (container) {
        const rect = container.getBoundingClientRect()
        const width = Math.floor(rect.width)
        const height = Math.floor(rect.height)
        
        if (width > 0 && height > 0) {
          setCanvasSize({ width, height })
          canvasSizeRef.current = { width, height }
        }
      }
    }

    updateCanvasSize()
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateCanvasSize)
    })
    
    const container = document.querySelector('.canvas-container')
    if (container) {
      resizeObserver.observe(container)
    }

    window.addEventListener('resize', updateCanvasSize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateCanvasSize)
    }
  }, [])

  // Handle adjustments
  const handleAdjustmentsChange = useCallback((adjustments) => {
    setCurrentAdjustments(adjustments)
  }, [])

  // Handle version saving
  const handleSaveVersion = useCallback(async (version) => {
    if (!selectedGroup) return

    try {
      const updatedGroup = {
        ...selectedGroup,
        versions: [...selectedGroup.versions, version],
        totalVersions: selectedGroup.totalVersions + 1,
        lastUpdatedAt: new Date().toISOString()
      }

      setGroups(prev => prev.map(g => 
        g.groupId === selectedGroup.groupId ? updatedGroup : g
      ))

      setSelectedGroup(updatedGroup)
    } catch (error) {
      console.error('Failed to save version:', error)
    }
  }, [selectedGroup])

  // Update imagePreviewRef
  useEffect(() => {
    setImagePreviewRef(imagePreviewRef)
  }, [imagePreviewRef, setImagePreviewRef])

  // Reset edit state when group changes
  useEffect(() => {
    reset()
  }, [selectedGroup, reset])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        if (e.shiftKey) {
          e.preventDefault()
          redo()
        } else {
          e.preventDefault()
          undo()
        }
      }
    }

    window.addEventListener('keydown', handleKeyboard)
    return () => window.removeEventListener('keydown', handleKeyboard)
  }, [undo, redo])

  // Add export handler
  const handleExport = useCallback(() => {
    if (!imagePreviewRef.current || !selectedGroup) {
      console.warn('No image to export')
      return null
    }
    
    return imagePreviewRef.current.exportImage?.() || 
      imagePreviewRef.current.getAdjustedImageData()
  }, [selectedGroup])

  // Add brush stroke handler
  const handleBrushStroke = useCallback((stroke) => {
    if (!stroke || !canvasSize.width || !canvasSize.height) return

    setMaskLayer(prevLayer => {
      if (!prevLayer) {
        // Initialize new layer if none exists
        return new ImageData(
          new Uint8ClampedArray(canvasSize.width * canvasSize.height * 4),
          canvasSize.width,
          canvasSize.height
        )
      }

      // Create a new canvas to draw the stroke
      const canvas = document.createElement('canvas')
      canvas.width = canvasSize.width
      canvas.height = canvasSize.height
      const ctx = canvas.getContext('2d')

      // Draw previous layer
      const imageData = new ImageData(
        new Uint8ClampedArray(prevLayer.data),
        prevLayer.width,
        prevLayer.height
      )
      ctx.putImageData(imageData, 0, 0)

      // Draw new stroke
      if (stroke.type === 'dot') {
        ctx.beginPath()
        ctx.arc(stroke.point.x, stroke.point.y, stroke.width / 2, 0, Math.PI * 2)
        ctx.fillStyle = stroke.mode === 'erase' ? 'black' : 'white'
        ctx.fill()
      } else if (stroke.type === 'stroke' && stroke.from && stroke.to) {
        ctx.beginPath()
        ctx.moveTo(stroke.from.x, stroke.from.y)
        ctx.lineTo(stroke.to.x, stroke.to.y)
        ctx.strokeStyle = stroke.mode === 'erase' ? 'black' : 'white'
        ctx.lineWidth = stroke.width
        ctx.lineCap = 'round'
        ctx.stroke()
      }

      return ctx.getImageData(0, 0, canvasSize.width, canvasSize.height)
    })
  }, [canvasSize])

  // Add brush settings handler
  const handleBrushSettingsChange = useCallback((settings) => {
    console.log('Brush settings changed:', settings)
    
    switch (settings.action) {
      case 'start':
        console.log('Starting brush mode')
        const currentSize = canvasSizeRef.current
        
        if (currentSize.width <= 0 || currentSize.height <= 0) {
          console.error('Invalid canvas size, retrying...')
          // Force a canvas size update and retry
          const container = document.querySelector('.canvas-container')
          if (container) {
            const rect = container.getBoundingClientRect()
            const width = Math.floor(rect.width)
            const height = Math.floor(rect.height)
            
            if (width > 0 && height > 0) {
              setCanvasSize({ width, height })
              canvasSizeRef.current = { width, height }
              // Retry brush initialization after a short delay
              setTimeout(() => {
                setIsBrushing(true)
                try {
                  setMaskLayer(new ImageData(width, height))
                } catch (error) {
                  console.error('Failed to create mask layer:', error)
                }
              }, 50)
              return
            }
          }
          console.error('Could not initialize canvas size')
          return
        }
        
        setIsBrushing(true)
        console.log('Brushing mode started with canvas size:', currentSize)
        try {
          setMaskLayer(new ImageData(currentSize.width, currentSize.height))
        } catch (error) {
          console.error('Failed to create mask layer:', error)
        }
        break
        
      case 'mode':
        console.log('Changing brush mode to:', settings.mode)
        setBrushMode(settings.mode)
        break
        
      case 'complete':
        const completedMask = imagePreviewRef.current?.getBrushMask()
        if (completedMask) {
          console.log('Mask completed:', completedMask)
        }
        setIsBrushing(false)
        setMaskLayer(null)
        break
        
      case 'export':
        const exportedMask = imagePreviewRef.current?.getBrushMask()
        if (exportedMask) {
          return exportedMask
        }
        break
        
      case 'stop':
        console.log('Stopping brush mode')
        setIsBrushing(false)
        setMaskLayer(null)
        break
      default:
        break
    }
  }, [])

  // Add a handler for deletion
  const handleDeleteGroup = useCallback(async (groupId) => {
    try {
      // Find the current group's index
      const currentIndex = groups.findIndex(g => g.groupId === groupId)
      
      // Find the next group to select
      let nextGroup = null
      if (currentIndex < groups.length - 1) {
        // Get next group
        nextGroup = groups[currentIndex + 1]
      } else if (currentIndex > 0) {
        // Get previous group if we're at the end
        nextGroup = groups[currentIndex - 1]
      }

      // Delete the group
      await generationsApi.deleteGroup(groupId)
      
      // Update local state
      setGroups(prev => prev.filter(g => g.groupId !== groupId))
      
      // Select next group if available
      if (nextGroup) {
        selectGroup(nextGroup)
      } else {
        // No more groups, clear selection
        selectGroup(null)
      }
      
      // Show success message
      addToast('Photo deleted successfully', 'success')
    } catch (error) {
      console.error('Failed to delete group:', error)
      addToast(error.message || 'Failed to delete photo', 'error')
    }
  }, [groups, selectGroup])

  return (
    <div className="App flex flex-col h-screen overflow-hidden bg-gray-950">
      <StatusBar onExport={handleExport} hasImage={!!selectedGroup} />
      <div className="flex-1 flex relative mt-8">
        {selectedGroup ? (
          <>
            <div className="flex-1 flex flex-col">
              <PromptBar 
                prompt={selectedGroup.prompt} 
                rightPanelVisible={rightPanelVisible} 
              />
              <div className="canvas-container flex-1 bg-gray-900 relative overflow-hidden mb-24 flex items-center justify-center">
                <ImagePreview 
                  ref={imagePreviewRef}
                  selectedImage={selectedGroup.selectedVersion}
                  canvasSize={canvasSize}
                  adjustments={currentAdjustments}
                  onError={(error) => setImageError(error)}
                  rightPanelVisible={rightPanelVisible}
                  isBrushing={isBrushing}
                  brushMode={brushMode}
                  maskLayer={maskLayer}
                  onBrushStroke={handleBrushStroke}
                  onCanvasSizeChange={setCanvasSize}
                  showMaskPreview={showMaskPreview}
                  previewMaskId={selectedMaskId}
                />
              </div>
            </div>
            <RightPanel 
              selectedImage={selectedGroup.selectedVersion}
              onAdjustmentsChange={handleAdjustmentsChange}
              onSaveVersion={handleSaveVersion}
              isVisible={rightPanelVisible}
              imagePreviewRef={imagePreviewRef}
              isPainting={isPainting}
              onBrushSettingsChange={handleBrushSettingsChange}
              onDeleteGroup={handleDeleteGroup}
              onMaskSelect={(maskId) => {
                setSelectedMaskId(maskId)
                setShowMaskPreview(false)
              }}
              onPreviewToggle={(show) => setShowMaskPreview(show)}
              showMaskPreview={showMaskPreview}
              selectedMaskId={selectedMaskId}
            />
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center bg-gray-900">
            <p className="text-xl text-gray-300 font-medium mb-2">
              Select a photo to start editing
            </p>
            <p className="text-sm text-gray-500 mb-4">
              - or -
            </p>
            <button
              onClick={handleAddClick}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              Generate new photos
            </button>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10">
        <div className="h-24 bg-gray-950 border-t border-gray-800">
          <BottomFilmstrip 
            groups={groups}
            selectImage={selectGroup}
            onAddClick={handleAddClick}
            selectedGroup={selectedGroup}
            rightPanelVisible={rightPanelVisible}
          />
        </div>
      </div>

      <AnimatePresence>
        {showNewPhotoPanel && (
          <div className="absolute inset-0 z-30">
            <div 
              className="fixed inset-0" 
              onClick={() => setShowNewPhotoPanel(false)}
            />
            <NewPhotoPanel
              onClose={() => setShowNewPhotoPanel(false)}
              onGenerate={handleGenerate}
            />
          </div>
        )}
      </AnimatePresence>
      
      <ToastContainer />
    </div>
  )
}

export default Editor